import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Container, Stack, Button, Spinner, Card } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  banGuardian,
  banMember,
  fetchCase,
  fetchCaseStatuses,
  fetchCMUsers,
  fetchEthnicities,
  fetchGenders,
  fetchJobs,
  fetchPrograms,
  fetchRaces,
  fetchRelationships,
  fetchUserCaseGuardians,
  fetchUserCaseMembers,
  patchCase,
  patchGuardian,
  patchMember,
  postNote,
} from "../../services/httpClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import PageTitle from "../ui/PageTitle";
import SelectObject from "../ui/SelectObject";
import ToastMsg from "../ui/ToastMsg";
import Input from "../ui/Input";
import SaveChangesModal from "../ui/SaveChangesModal";
import { unsavedChanges, currentUrl } from "../../App";
import useApp from "../../utils/useApp";
import states from "../../data/states.json";
import { User } from "../../types/user";
import HouseholdMemberList from "../household-ui/HouseholdMemberList";
import CreateMember from "../household-ui/CreateMember";
import EditMember from "../household-ui/EditMember";
import ConfirmDelete from "../household-ui/ConfirmDelete";
import ConfirmBan from "../ui/ConfirmBan";
import ExitNote from "../ui/ExitNote";
import IntakeNote from "../ui/IntakeNote";
import { personHousing } from "../../helpers/housingAssignment";
import useHousingInventory from "../../utils/useHousingInventory";

interface State {
  key: string;
  name: string;
}

interface EditCaseState {
  limitedAccess: boolean;
}

const EditCase = ({ limitedAccess }: EditCaseState) => {
  const { showMenu, setActivePage }: any = useApp();
  const location = useLocation();
  currentUrl.value = location.pathname;
  const { id } = useParams();
  const [name, setName] = useState<string>("");
  const [memberSave, setMemberSave] = useState<{ enabled: boolean; save: (isDone: boolean) => void }>({ enabled: false, save: () => {} });
  const [primaryCM, setPrimaryCM] = useState<string>("");
  const [secondaryCM, setSecondaryCM] = useState<string>("");
  const [initialStatus, setInitialStatus] = useState<string>("");
  const [status, setStatus] = useState<string>("inactive");
  const [addressOne, setAddressOne] = useState<string>("");
  const [addressTwo, setAddressTwo] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [zip, setZip] = useState<string>("");
  const [isBanned, setIsBanned] = useState<boolean>(false);
  const [duration, setDuration] = useState<string>("indefinite");
  const statesList: State[] = states;
  const [cmUsers, setCmUsers] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [caseData, setCaseData] = useState<any>();
  const [family, setFamily] = useState<any[]>([]);
  const [familyLoaded, setFamilyLoaded] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<any>();
  const [relationships, setRelationships] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [ethnicities, setEthnicities] = useState<any[]>([]);
  const [races, setRaces] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [lookupsLoaded, setLookupsLoaded] = useState<boolean>(false);
  const inventory = useHousingInventory();
  const { rooms, beds, dwellings, facilities } = inventory;
  const [showIntake, setShowIntake] = useState<boolean>(false);
  const [intakeDate, setIntakeDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [intakeNote, setIntakeNote] = useState<string>("");
  const [showExit, setShowExit] = useState<boolean>(false);
  const [exitDate, setExitDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [exitNote, setExitNote] = useState<string>();
  const [exitType, setExitType] = useState<string>("voluntary");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [create, setCreate] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isBanning, setIsBanning] = useState<boolean>(false);
  const [isSavingMember, setIsSavingMember] = useState<boolean>(false);
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");
  const [leavePath, setLeavePath] = useState<string>("/cases");
  const [userToSave, setUserToSave] = useState<boolean>(false);
  const arrivalMessageShown = useRef<boolean>(false);

  let navigate = useNavigate();

  useEffect(() => {
    if (userToSave) {
      unsavedChanges.value = true;
    } else {
      unsavedChanges.value = false;
    }
  }, [userToSave]);

  const backCase = () => {
    if (saveIsEnabled) {
      setLeavePath("/cases");
      setShowModal(true);
    } else {
      navigate(`/cases`);
    }
  };

  useEffect(() => {
    getCase();
    getFamily();
    Promise.all([
      fetchRelationships().then((res: any) => setRelationships(res[0].data)),
      fetchGenders().then((res: any) => setGenders(res[0].data)),
      fetchEthnicities().then((res: any) => setEthnicities(res[0].data)),
      fetchRaces().then((res: any) => setRaces(res[0].data)),
      fetchPrograms().then((res: any) => setPrograms(res[0].data)),
      fetchJobs().then((res: any) => setJobs(res[0].data)),
    ])
      .catch((err: any) => console.log(err))
      .finally(() => setLookupsLoaded(true));
  }, []);

  const getCase = () => {
    setIsLoading(true);
    getCaseStatuses();

    fetchCase(id)
      .then((res: any) => {
        const currentCase = res[0].data;
        setCaseData(currentCase);
        setName(currentCase.name);
        setPrimaryCM(currentCase.primaryCM?.user_id);
        setSecondaryCM(currentCase.secondaryCM?.user_id);
        setStatus(currentCase.casestatus);
        setAddressOne(currentCase.address_line_one);
        setAddressTwo(currentCase.address_line_two);
        setCity(currentCase.city);
        setState(currentCase.state);
        setZip(currentCase.zip);

        setInitialStatus(currentCase.casestatus);
        //reset intake and exit
        setShowIntake(false);
        setShowExit(false);
        setIntakeNote("");
        setIntakeDate(new Date().toISOString().split("T")[0]);
        setExitNote("");
        setExitDate(new Date().toISOString().split("T")[0]);
        setExitType("voluntary");
        setIsBanned(false);
        setDuration("indefinite");

        fetchCMUsers()
          .then((res: any) => {
            const users = res[0].data.map((i: any) => {
              return {
                id: i.id,
                key: i.id,
                name: i.firstname.length === 0 ? i.email : `${i.firstname} ${i.lastname ?? ""}`,
              };
            });
            setCmUsers(users);
          })
          .finally(() => {
            setIsLoading(false);
            // the toast or error handed over by the screen we arrived from (e.g. "Case Created") is for the
            // arrival only; getCase runs again after every save and must not bring it back
            if (!arrivalMessageShown.current) {
              arrivalMessageShown.current = true;
              if (location.state?.errorSummary) {
                setErrMsg(location.state.errorSummary);
              } else if (location.state?.toastText) {
                setShowToast(true);
                setToastText(location.state?.toastText);
                setToastBody(location.state?.toastBody);
              }
            }
          });
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  // everyone on the case, banned and deleted included, the same way the Household tab loads it
  const getFamily = () => {
    return fetchUserCaseGuardians(id, true)
      .then((res: any) => {
        const guardians = res[0].data;
        return fetchUserCaseMembers(id, true).then((res: any) => {
          setFamily([...guardians, ...res[0].data]);
        });
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setFamilyLoaded(true);
      });
  };

  // keep the open person in step with a refreshed family
  useEffect(() => {
    if (!currentUser) return;
    const updated = family.find((f: any) => f.id === currentUser.id && f.type === currentUser.type);
    if (updated) setCurrentUser(updated);
  }, [family]);

  const active = useMemo(() => family.filter((f: any) => !f.is_banned && !f.deleted_at), [family]);
  const peopleServedAndPrimary = useMemo(() => active.filter((f: any) => f.is_case_subject && f.is_primary), [active]);
  const peopleServed = useMemo(() => active.filter((f: any) => f.is_case_subject && !f.is_primary), [active]);
  const primaryContact = useMemo(() => active.filter((f: any) => !f.is_case_subject && f.is_primary), [active]);
  const primaryAdults = useMemo(() => active.filter((f: any) => f.role === "family" && !f.is_primary && !f.is_case_subject), [active]);
  const members = useMemo(() => active.filter((f: any) => f.role !== "family" && !f.is_case_subject), [active]);
  const otherAdults = useMemo(() => members.filter((m: any) => m.relationship !== "child"), [members]);
  const children = useMemo(() => members.filter((m: any) => m.relationship === "child"), [members]);
  const banned = useMemo(() => family.filter((f: any) => f.is_banned && !f.deleted_at), [family]);
  const deleted = useMemo(() => family.filter((f: any) => f.deleted_at), [family]);
  const householdCount = useMemo(() => family.filter((f: any) => !f.deleted_at).length, [family]);

  // the people a status change (exit, intake, ban) applies to: everyone not deleted
  const guardians = useMemo(() => family.filter((f: any) => f.type === "guardian" && !f.deleted_at), [family]);
  const household = useMemo(
    () => family.filter((f: any) => f.type !== "guardian" && f.relationship !== "child" && !f.deleted_at),
    [family],
  );
  const kids = useMemo(() => family.filter((f: any) => f.type !== "guardian" && f.relationship === "child" && !f.deleted_at), [family]);

  useEffect(() => {
    if (saveIsEnabled) {
      unsavedChanges.value = true;
    } else {
      unsavedChanges.value = false;
    }
  }, [saveIsEnabled]);

  const getCaseStatuses = async () => {
    await fetchCaseStatuses().then((res: any) => {
      setStatuses(res[0].data);
    });
  };

  const saveCase = async () => {
    setIsLoading(true);
    setErrMsg("");

    const updatedCase = {
      name: name,
      primaryCM: primaryCM,
      secondaryCM: secondaryCM,
      casestatus: status,
      address_line_one: addressOne,
      address_line_two: addressTwo,
      city: city,
      state: state,
      zip: zip,
    };

    patchCase(updatedCase, id)
      .then((res: any) => {
        setSaveIsEnabled(false);
        setShowToast(true);
        setToastText("Case Updated");
        setToastBody(name);
      })
      .catch((err: any) => {
        console.log(err);
        setErrMsg(err?.response?.data?.msg);
      });

    //this means the cases is being deactivated
    if (initialStatus === "active" && status !== "active") {
      if (isBanned) {
        exitOnStatusChange();
        await Promise.all([banOnStatusChange()]);
      } else {
        await Promise.all([exitOnStatusChange()]);
      }
    } else if (initialStatus !== "active" && status === "active") {
      //reactivating case
      await Promise.all([intakeOnStatusChange()]);
    }

    setTimeout(() => {
      getCase();
      getFamily();
    }, 1500);
  };

  const exitOnStatusChange = () => {
    guardians.forEach((g: any) => {
      const note = {
        notetype: "exit",
        subject: {
          id: g?.id,
          type: "guardian",
        },
        fields: {
          exit_type: exitType,
          description: exitNote ?? "",
          date: new Date().toISOString().split("T")[0],
        },
      };

      //save exit note
      postNote(note, id)
        .then((res: any) => {
          const noteId = res[0].data.note_id;

          //update user exit data
          const exitData = {
            exit_at: exitDate,
            exit_note_id: noteId,
          };

          patchGuardian(exitData, id, g?.id).catch((err: any) => {
            console.log(err);
          });
        })
        .catch((err: any) => {
          console.log(err);
        });
    });

    household.forEach((h: any) => {
      const note = {
        notetype: "exit",
        subject: {
          id: h?.id,
          type: "member",
        },
        fields: {
          exit_type: exitType,
          description: exitNote ?? "",
          date: new Date().toISOString().split("T")[0],
        },
      };

      //save exit note
      postNote(note, id)
        .then((res: any) => {
          const noteId = res[0].data.note_id;

          //update user exit data
          const exitData = {
            exit_at: exitDate,
            exit_note_id: noteId,
          };

          patchMember(exitData, id, h?.id).catch((err: any) => {
            console.log(err);
          });
        })
        .catch((err: any) => {
          console.log(err);
        });
    });

    kids.forEach((c: any) => {
      const note = {
        notetype: "exit",
        subject: {
          id: c?.id,
          type: "member",
        },
        fields: {
          exit_type: exitType,
          description: exitNote ?? "",
          date: new Date().toISOString().split("T")[0],
        },
      };

      //save exit note
      postNote(note, id)
        .then((res: any) => {
          const noteId = res[0].data.note_id;

          //update user exit data
          const exitData = {
            exit_at: exitDate,
            exit_note_id: noteId,
          };

          patchMember(exitData, id, c?.id).catch((err: any) => {
            console.log(err);
          });
        })
        .catch((err: any) => {
          console.log(err);
        });
    });

    setShowExit(false);
  };

  const intakeOnStatusChange = () => {
    guardians.forEach((g: any) => {
      const note = {
        notetype: "intake",
        subject: {
          id: g?.id,
          type: "guardian",
        },
        fields: {
          description: intakeNote,
          date: new Date().toISOString().split("T")[0],
        },
      };

      //save intake note
      postNote(note, id)
        .then((res: any) => {
          const noteId = res[0].data.note_id;

          //update user intake data
          const intakeData = {
            intake_at: intakeDate,
            intake_note_id: noteId,
            exit_at: null,
            exit_note_id: null,
          };

          patchGuardian(intakeData, id, g?.id).catch((err: any) => {
            console.log(err);
          });
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg || "Failed to save note");
        });
    });

    household.forEach((h: any) => {
      const note = {
        notetype: "intake",
        subject: {
          id: h?.id,
          type: "member",
        },
        fields: {
          description: intakeNote,
          date: new Date().toISOString().split("T")[0],
        },
      };

      //save intake note
      postNote(note, id)
        .then((res: any) => {
          const noteId = res[0].data.note_id;

          //update member intake data
          const intakeData = {
            intake_at: intakeDate,
            intake_note_id: noteId,
            exit_at: null,
            exit_note_id: null,
          };

          patchMember(intakeData, id, h.id).catch((err: any) => {
            console.log(err);
          });
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg || "Failed to save note");
        });
    });

    kids.forEach((c: any) => {
      const note = {
        notetype: "intake",
        subject: {
          id: c?.id,
          type: "member",
        },
        fields: {
          description: intakeNote,
          date: new Date().toISOString().split("T")[0],
        },
      };

      //save intake note
      postNote(note, id)
        .then((res: any) => {
          const noteId = res[0].data.note_id;

          //update member intake data
          const intakeData = {
            intake_at: intakeDate,
            intake_note_id: noteId,
            exit_at: null,
            exit_note_id: null,
          };

          patchMember(intakeData, id, c.id).catch((err: any) => {
            console.log(err);
          });
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg || "Failed to save note");
        });
    });

    setShowExit(false);
  };

  const banOnStatusChange = async () => {
    guardians.map((g: any) => {
      const banObj = {
        is_banned: true,
        duration: duration,
        program: null,
        previous_job: g?.job,
        job: null,
      };

      banGuardian(banObj, id, g?.id).catch((err: any) => {
        console.log(err);
      });
    });

    household.map((h: any) => {
      performBanMember(h);
    });

    kids.map((c: any) => {
      performBanMember(c);
    });
  };

  const performBanMember = (m: any) => {
    const banObj = {
      is_banned: true,
      duration: duration,
      program: null,
      previous_job: m?.job,
      job: null,
    };

    banMember(banObj, id, m?.id).catch((err: any) => {
      console.log(err);
    });
  };

  const updateName = (e: any) => {
    setErrMsg("");
    setName(e.target.value);
    if (e.target.value) {
      setSaveIsEnabled(true);
    } else {
      setSaveIsEnabled(false);
    }
  };

  const updatePrimaryCM = (e: any) => {
    if (e.target.value == secondaryCM && e.target.value !== "0") {
      setErrMsg("Primary Case Manager cannot be the same as Secondary Case Manager");
    } else {
      setPrimaryCM(e.target.value);
      enableSave();
    }
  };

  const updateSecondaryCM = (e: any) => {
    if (e.target.value == primaryCM && e.target.value !== "0") {
      setErrMsg("Secondary Case Manager cannot be the same as Primary Case Manager");
    } else {
      setSecondaryCM(e.target.value);
      enableSave();
    }
  };

  const updateCaseStatus = (e: any) => {
    setStatus(e.target.value);

    if (initialStatus === "active" && e.target.value !== "active") {
      setShowExit(true);
      setShowIntake(false);
    } else if (initialStatus !== "active" && e.target.value === "active") {
      setShowExit(false);
      setShowIntake(true);
    } else {
      setShowExit(false);
      setShowIntake(false);
    }

    enableSave();
  };

  const updateAddressOne = (e: any) => {
    setAddressOne(e.target.value);
    enableSave();
  };

  const updateAddressTwo = (e: any) => {
    setAddressTwo(e.target.value);
    enableSave();
  };

  const updateCity = (e: any) => {
    setCity(e.target.value);
    enableSave();
  };

  const updateState = (e: any) => {
    setState(e.target.value);
    enableSave();
  };

  const updateZip = (e: any) => {
    setZip(e.target.value);
    enableSave();
  };

  const onClickCreateFamily = () => {
    setErrMsg("");
    setCurrentUser(undefined);
    setCreate(true);
  };

  function enableSave() {
    setErrMsg("");
    if (name) {
      setSaveIsEnabled(true);
    } else {
      setSaveIsEnabled(false);
    }
  }

  const onClickGoToCase = () => {
    if (saveIsEnabled) {
      setLeavePath(`/my-cases/${id}`);
      setShowModal(true);
    } else {
      navigate(`/my-cases/${id}`);
      setActivePage("/my-cases");
    }
  };

  // leaving the add or edit pane: show the case straight away and refresh the household behind it
  const onLeaveMember = () => {
    setCreate(false);
    setCurrentUser(undefined);
    setUserToSave(false);
    setShowModal(false);
    setIsSavingMember(false);
    getFamily();
  };

  const onClickDoneEdit = (forceNoModal: boolean = false) => {
    if (!forceNoModal && userToSave) {
      setShowModal(true);
    } else {
      onLeaveMember();
    }
  };

  // back out of a person (after deleting or banning) to the case itself
  const resetHouseholdTab = () => {
    setCreate(false);
    setIsDeleting(false);
    setIsBanning(false);
    setUserToSave(false);
    setCurrentUser(undefined);
    getFamily();
  };

  // where the selected person sleeps, worked out once for both the view and edit panes
  const housing =
    currentUser && (dwellings.length > 0 || rooms.length > 0)
      ? personHousing({
          caseId: id,
          person: currentUser,
          dwellings,
          rooms,
          facilities,
          beds,
        })
      : { kind: "" as const, place: "", label: "" };

  // The person panes speak about the case's dwelling or room only; an individual bed is
  // managed in the Housing card on the Household tab.
  const caseLevelHousing = housing.kind === "dwelling" || housing.kind === "room" ? housing : { kind: "" as const, place: "", label: "" };

  // members are judged against the case as saved, not the status being edited above
  const savedCase = { ...caseData, casestatus: initialStatus };
  const possiblePrimaryContacts = [...peopleServedAndPrimary, ...peopleServed, ...primaryContact, ...primaryAdults];
  const memberMode = create || !!currentUser;
  const pageLoading = isLoading || !(familyLoaded && lookupsLoaded && inventory.hasLoadedOnce);

  const spinner = (
    <Container>
      <Button size="sm" className="spinner-button spinner-button-main">
        <Spinner animation="border" style={{ color: "#F6893D" }} />
      </Button>
    </Container>
  );

  // clicking a card opens that person straight in the edit pane
  const memberList = (createMode: boolean) => (
    <HouseholdMemberList
      peopleServedAndPrimary={peopleServedAndPrimary}
      peopleServed={peopleServed}
      primaryContact={primaryContact}
      primaryAdults={primaryAdults}
      otherAdults={otherAdults}
      children={children}
      banned={banned}
      deleted={deleted}
      currentUser={currentUser}
      setCurrentUser={setCurrentUser}
      userToSave={userToSave}
      setShowModal={setShowModal}
      createMode={createMode}
    />
  );

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />

      {pageLoading ? (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <Container className={showMenu ? "lg div-main-menu pb-5" : "lg div-main pb-5"}>
          {create ? (
            <>
              <PageTitle
                title="Create Household Member"
                backButtonText={`Back to ${name || "Case"}`}
                onBackClick={() => onClickDoneEdit()}
                actions={[
                  { title: "Save and Add Another", onClick: () => memberSave.save(false), color: "secondary", disabled: !memberSave.enabled || isSavingMember },
                  { title: "Save and Close", onClick: () => memberSave.save(true), color: "secondary", disabled: !memberSave.enabled || isSavingMember },
                ]}
              />
              <Row className="flex-column flex-lg-row">
                <Col xs={12} lg={4} xl={3} className="mb-3">
                  <Card className="case-household-panel">
                    <Card.Body>
                      <div className="d-flex flex-column gap-2">{memberList(true)}</div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={12} lg={8} xl={9} className="ps-3 pt-lg-3">
                  {isSavingMember ? (
                    spinner
                  ) : (
                    <CreateMember
                      originalRooms={rooms}
                      originalBeds={beds}
                      possiblePrimaryContacts={possiblePrimaryContacts}
                      currentCase={savedCase}
                      relationships={relationships}
                      genders={genders}
                      ethnicities={ethnicities}
                      races={races}
                      originalPrograms={programs}
                      myCase={true}
                      setUserToSave={setUserToSave}
                      getFamily={getFamily}
                      onClickDoneEdit={onClickDoneEdit}
                      setIsSaving={setIsSavingMember}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      onSaveStateChange={setMemberSave}
                    />
                  )}
                </Col>
              </Row>
            </>
          ) : memberMode ? (
            isSavingMember ? (
              spinner
            ) : isDeleting ? (
              <ConfirmDelete
                user={currentUser}
                family={active}
                setIsDeleting={setIsDeleting}
                fetchHousehold={getFamily}
                resetHouseholdTab={resetHouseholdTab}
                caseStatus={savedCase?.casestatus}
                isLastUser={householdCount === 1}
                setShowToast={setShowToast}
                setToastText={setToastText}
                setToastBody={setToastBody}
              />
            ) : isBanning ? (
              <ConfirmBan
                user={currentUser}
                previousJob={currentUser?.job?.key ?? ""}
                isBanned={currentUser?.is_banned === 1}
                setIsBanning={setIsBanning}
                fetchHousehold={getFamily}
                resetCurrentView={resetHouseholdTab}
                type={currentUser?.type}
                caseStatus={savedCase?.casestatus}
                setShowToast={setShowToast}
                setToastText={setToastText}
                setToastBody={setToastBody}
              />
            ) : (
              <>
                <Stack direction="horizontal" gap={2} className="cursor-pointer" onClick={() => onClickDoneEdit()}>
                  <FontAwesomeIcon icon={faArrowLeft} className="me-0 mb-1 app-tertiary-color-inverse cursor-pointer" fontSize={16} />
                  <h6 className="app-tertiary-color-inverse">{`Back to ${name || "Case"}`}</h6>
                </Stack>
                <EditMember
                  user={currentUser}
                  housing={caseLevelHousing}
                  possiblePrimaryContacts={possiblePrimaryContacts}
                  currentCase={savedCase}
                  relationships={relationships}
                  genders={genders}
                  ethnicities={ethnicities}
                  races={races}
                  rooms={rooms}
                  originalPrograms={programs}
                  originalJobs={jobs}
                  myCase={true}
                  setUserToSave={setUserToSave}
                  getFamily={getFamily}
                  onClickDoneEdit={onClickDoneEdit}
                  setIsSaving={setIsSavingMember}
                  setShowToast={setShowToast}
                  setToastText={setToastText}
                  setToastBody={setToastBody}
                  onClickBan={() => setIsBanning(true)}
                  onClickDelete={() => setIsDeleting(true)}
                />
              </>
            )
          ) : (
            <>
              <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
                {errMsg}
              </p>
              <Stack direction="horizontal" gap={2} className="cursor-pointer" onClick={() => backCase()}>
                <FontAwesomeIcon icon={faArrowLeft} className="me-0 mb-1 app-tertiary-color-inverse cursor-pointer" fontSize={16} />
                <h6 className="app-tertiary-color-inverse">{"Back to Cases"}</h6>
              </Stack>
              <Row className="mb-3 align-items-center">
                <Col xs={12} md={5}>
                  <h2 className="hero-heading app-header-text mb-md-0">Edit Case</h2>
                </Col>
                <Col xs={12} md={7} className="text-md-end">
                  <Button className="app-primary-color me-3 button" onClick={(e) => onClickCreateFamily()}>
                    Add Member
                  </Button>
                  {!limitedAccess && (
                    <Button id="pagetitle-button" className={"app-primary-color button me-3"} onClick={onClickGoToCase}>
                      {"View Case"}
                    </Button>
                  )}
                  <Button id="pagetitle-button" className={"app-secondary-color button"} onClick={saveCase} disabled={!saveIsEnabled}>
                    Save
                  </Button>
                </Col>
              </Row>
              <Row className="flex-column flex-lg-row">
                <Col xs={12} lg={8} xl={9} className="mb-3">
                  <h4 className="app-header-text mt-0">Details</h4>
                  <Stack gap={3}>
                    <Input
                      label="Name"
                      type="text"
                      value={name}
                      onUpdate={(e: any) => updateName(e)}
                      required={true}
                      showCharCount
                      maxLength={75}
                    />
                    <Row>
                      <Col xs={6}>
                        <SelectObject
                          label="Primary Case Manager"
                          value={primaryCM}
                          options={cmUsers}
                          onUpdate={(e: any) => updatePrimaryCM(e)}
                        />
                      </Col>
                      <Col xs={6}>
                        <SelectObject
                          label="Secondary Case Manager"
                          value={secondaryCM}
                          options={cmUsers}
                          onUpdate={(e: any) => updateSecondaryCM(e)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={6}>
                        <SelectObject
                          label="Status"
                          value={status}
                          options={statuses}
                          onUpdate={(e: any) => updateCaseStatus(e)}
                          skipInitialOption={true}
                        />
                      </Col>
                    </Row>
                  </Stack>
                  {showExit && (
                    <Row className="mt-3">
                      <ExitNote
                        exitDate={exitDate}
                        setExitDate={setExitDate}
                        exitNote={exitNote}
                        setExitNote={setExitNote}
                        exitType={exitType}
                        setExitType={setExitType}
                        includeBan={true}
                        isBanned={isBanned}
                        setIsBanned={setIsBanned}
                        duration={duration}
                        setDuration={setDuration}
                      />
                    </Row>
                  )}
                  {showIntake && (
                    <Row className="mt-3">
                      <IntakeNote
                        intakeDate={intakeDate}
                        setIntakeDate={setIntakeDate}
                        intakeNote={intakeNote}
                        setIntakeNote={setIntakeNote}
                        focus={true}
                      />
                    </Row>
                  )}

                  <h4 className="app-header-text mt-4">Address</h4>
                  <Stack gap={3}>
                    <Input label="Address Line One" type="text" value={addressOne} onUpdate={(e: any) => updateAddressOne(e)} />
                    <Input label="Address Line Two" type="text" value={addressTwo} onUpdate={(e: any) => updateAddressTwo(e)} />
                    <Row>
                      <Col xs={4}>
                        <Input label="City" type="text" value={city} onUpdate={(e: any) => updateCity(e)} />
                      </Col>
                      <Col xs={4}>
                        <SelectObject label="State" value={state} options={statesList} onUpdate={(e: any) => updateState(e)} />
                      </Col>
                      <Col xs={4}>
                        <Input label="Zip" type="text" value={zip} onUpdate={(e: any) => updateZip(e)} />
                      </Col>
                    </Row>
                  </Stack>
                </Col>
                <Col xs={12} lg={4} xl={3} className="mb-3">
                  <Card className="case-household-panel">
                    <Card.Body>
                      {family.length > 0 ? (
                        <div className="d-flex flex-column gap-2">{memberList(false)}</div>
                      ) : (
                        <small className="app-blue-text">No household members yet</small>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          {memberMode ? (
            <SaveChangesModal showModal={showModal} setShowModal={setShowModal} onLeave={onLeaveMember} />
          ) : (
            <SaveChangesModal showModal={showModal} setShowModal={setShowModal} path={leavePath} />
          )}
        </Container>
      )}
    </>
  );
};

export default EditCase;
